import React from "react";
import { ArrowRight } from "lucide-react";

type GlyphProps = {
  className?: string;
  label: string;
  children: React.ReactNode;
};

const Glyph: React.FC<GlyphProps> = ({ className = "", label, children }) => (
  <span className={`nex-identity-glyph ${className}`} role="img" aria-label={label}>
    {children}
  </span>
);

const Roman2187Glyph: React.FC<{ className?: string; label: string }> = ({ className = "", label }) => (
  <span className={`nex-identity-glyph nex-glyph-2187__text ${className}`} role="img" aria-label={label}>
    <span className="nex-glyph-2187__semantic">ↇ</span>
    <svg className="nex-glyph-2187__vector" viewBox="0 0 1401 1356" aria-hidden="true" focusable="false">
      <path
        fill="currentColor"
        d="M1401 686Q1401 496 1320.5 340.0Q1240 184 1072.0 92.0Q904 0 643 0H36V38H87Q176 38 207 92Q217 109 223.0 146.0Q229 183 229 241V1117Q229 1242 203 1277Q160 1320 87 1320H36V1356H587Q812 1356 954.0 1318.5Q1096 1281 1192 1196Q1287 1112 1344.0 979.0Q1401 846 1401 686ZM1175 676Q1175 864 1104.5 999.5Q1034 1135 910.5 1208.0Q787 1281 627 1281Q583 1281 532.0 1274.5Q481 1268 422 1251V1169Q450 1177 485.5 1182.5Q521 1188 555 1188Q687 1188 798.5 1134.0Q910 1080 977.5 970.0Q1045 860 1045 691Q1045 541 987.0 418.0Q929 295 793.0 221.5Q657 148 422 146V98Q485 85 538.0 77.0Q591 69 634 69Q787 68 909.5 141.0Q1032 214 1103.5 350.0Q1175 486 1175 676ZM422 266V212Q583 212 686.0 269.5Q789 327 838.0 424.0Q887 521 887 640Q887 718 867.0 800.0Q847 882 797.0 948.5Q747 1015 656.0 1049.0Q565 1083 422 1063V978Q436 981 455.0 984.5Q474 988 495 988Q623 988 699.0 897.5Q775 807 775 661Q775 482 678.5 382.0Q582 282 422 266ZM423 862H422V332H431Q512 332 564.5 408.0Q617 484 617 610Q617 729 567.5 795.5Q518 862 423 862Z"
        transform="translate(0 1356) scale(1 -1)"
      />
    </svg>
  </span>
);

export const NexIdentitySection: React.FC = () => (
  <section className="landing-identity-story landing-reveal-section">
    <div className="landing-identity-story__intro">
      <span className="landing-section-kicker">NEX ID / Alliance architecture</span>
      <h2><em>NEX ID</em><br />Your secure access credentials</h2>
      <p>Bring your observatory, people, tools, and snapshots into one clear working view while keeping control of what you share.</p>
      <button type="button" onClick={() => window.location.assign("/portal")} className="landing-text-button">
        Configure your NEX ID
        <ArrowRight />
      </button>
    </div>

    <div className="landing-identity-card">
      <div className="landing-identity-card__header">
        <span>Formation 001</span>
        <strong>Nexus Goodman</strong>
        <span>Private by default</span>
      </div>
      <div className="landing-identity-card__rule" aria-hidden="true" />
      <div className="landing-identity-glyphs" aria-label="Your NEX ID glyphs">
        <Glyph label="mind">🧠</Glyph>
        <Glyph label="force">⚡</Glyph>
        <Roman2187Glyph label="Roman numeral fifty thousand glyph" />
      </div>
      <small>Your NEX ID</small>
      <div className="landing-identity-card__dots" aria-hidden="true">•••</div>
      <div className="landing-identity-alliance" aria-label="Your alliance glyphs">
        <Glyph className="nex-identity-glyph--small nex-identity-glyph--ape" label="ape">🦍</Glyph>
        <Glyph className="nex-identity-glyph--small nex-identity-glyph--diamond" label="blue diamond">🔷</Glyph>
        <Glyph className="nex-identity-glyph--small nex-identity-glyph--turned-m" label="turned M">Ɯ</Glyph>
        <span className="nex-identity-separator" aria-hidden="true">::</span>
        <Glyph className="nex-identity-glyph--small nex-identity-glyph--sprout" label="sprout">🌱</Glyph>
        <Glyph className="nex-identity-glyph--small nex-identity-glyph--crown" label="crown">👑</Glyph>
        <Glyph className="nex-identity-glyph--small nex-identity-glyph--asterisk" label="asterism">⁂</Glyph>
        <span className="nex-identity-separator" aria-hidden="true">::</span>
        <Glyph className="nex-identity-glyph--small" label="mind">🧠</Glyph>
        <Glyph className="nex-identity-glyph--small" label="force">⚡</Glyph>
        <Roman2187Glyph className="nex-identity-glyph--small" label="Roman numeral fifty thousand glyph" />
      </div>
      <small>Your alliance</small>
      <div className="landing-identity-card__footer">
        <span>Observe</span><i aria-hidden="true" />
        <span>Coordinate</span><i aria-hidden="true" />
        <span>Record</span>
      </div>
    </div>

    <div className="landing-identity-principles">
      <article><span>01</span><h3>Identity</h3><p>A unique three-glyph signature for your observatory, snapshots, tools, and published work.</p></article>
      <article><span>02</span><h3>Formation</h3><p>Private alliances organize people, roles, workflows, calendars, and shared decisions.</p></article>
      <article><span>03</span><h3>NEX Ops</h3><p>Move from a recognizable symbol to a repeatable system your group can actually use.</p></article>
    </div>
  </section>
);
